import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LoadingButton } from '@mui/lab';
import { getPolyglot } from '../../../../i18n';
import StoerkIdAPI from '../../../../redux/stoerkID/StoerkIdAPI';
import { useAppDispatch, useAppSelector } from '../../../../redux/store.model';
import {
  StoerkIdUsageAPIResponse,
  WorkspaceAPI,
} from '../../../../redux/stoerkID/StoerkIdAPI.model';
import {
  LightTable,
  LightTableColumDef,
  LabelState,
} from '../../../../theme/components';
import { Link as LinkRouter } from 'react-router-dom';
import { Link } from '@mui/material';
import UserAPI from '../../../../redux/users/api/UserAPI';
import { getUserSelector } from '../../../../redux/users/api/User.selectors';
import useUsers from '../../../../redux/users/hooks/useUsers';
import useShowErrorMessage from '../../../../handlingErrors/useShowErrorMessage';
import useParseErrorMessage from '../../../../handlingErrors/useParseErrorMessage';
import LabelTitle from '../../../../theme/components/Forms/LabelTitle';
import { useUserRightUtil } from '../../../../util/rights';

interface SubWorkspacesOverviewProps {
  workspace: WorkspaceAPI;
}

function SubWorkspacesOverview({ workspace }: SubWorkspacesOverviewProps) {
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();

  const {
    data: workspaceChildren = [],
    isLoading,
    error: errorWorkspaceChildren,
  } = StoerkIdAPI.endpoints.getWorkspaceDescendants.useQuery(workspace.id);
  const parseErrorMessage = useParseErrorMessage();

  const childIds = useMemo(
    () => (workspaceChildren ? workspaceChildren.map((child) => child.id) : []),
    [workspaceChildren, workspace.id]
  );

  const hasChildren = childIds.length > 0;

  // Fetch usage for each child workspace
  useEffect(() => {
    childIds.forEach((childId) => {
      dispatch(
        StoerkIdAPI.endpoints.getWorkspaceUsage.initiate({
          workspaceId: childId,
          includeChildWorkspaces: true,
          includeLists: false,
        })
      );
    });
  }, [dispatch, childIds]);

  // Fetch stores usage for children with flexeserveUI.hierarchy setting
  useEffect(() => {
    workspaceChildren.forEach((child) => {
      if (child.settings?.['flexeserveUI.hierarchy']) {
        dispatch(
          StoerkIdAPI.endpoints.getWorkspaceStoresUsage.initiate({
            workspaceId: child.id,
            includeChildWorkspaces: true,
            includeStores: false,
          })
        );
      }
    });
  }, [dispatch, workspaceChildren]);

  // Check if any child has the flexeserveUI.hierarchy setting
  const showStoresColumn = useMemo(
    () =>
      workspaceChildren.some(
        (child) => child.settings?.['flexeserveUI.hierarchy']
      ),
    [workspaceChildren]
  );

  const childData = useAppSelector((state) =>
    workspaceChildren.map((child) => {
      const usageResponse = StoerkIdAPI.endpoints.getWorkspaceUsage.select({
        workspaceId: child.id,
        includeChildWorkspaces: true,
        includeLists: false,
      })(state);
      return {
        childId: child.id,
        workspace: child,
        usage: usageResponse.data,
        usageResponse,
      };
    })
  );

  // Edit quota dialog state
  const [editQuota, setEditQuota] = useState<{
    childId: string;
    users: number;
    devices: number;
  } | null>(null);

  const [updateQuota, { isLoading: isSavingQuota }] =
    StoerkIdAPI.useUpdateSubWorkspaceQuotaMutation();
  const showError = useShowErrorMessage();

  const handleSaveQuota = async () => {
    if (!editQuota) return;
    try {
      await updateQuota({
        workspaceId: workspace.id,
        subWorkspaceId: editQuota.childId,
        quota: { users: editQuota.users, devices: editQuota.devices },
      }).unwrap();
      setEditQuota(null);
    } catch (error) {
      showError(error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorWorkspaceChildren) {
    return (
      <Alert severity="error">
        {parseErrorMessage(errorWorkspaceChildren)}
      </Alert>
    );
  }

  if (!hasChildren) return null;

  return (
    <Box>
      <Typography variant="h2" mb={1}>
        {polyglot.t('stoerk_id.subworkspaces.title')}
      </Typography>

      {/* Quota summary */}
      {workspaceChildren.length > 0 && (
        <Box display="flex" gap={4} mb={2} flexWrap="wrap">
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.type_account')}
            </LabelTitle>
            <Typography variant="body2">
              {polyglot.t('stoerk_id.subworkspaces.type_organization')}
            </Typography>
          </Box>
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.accounts_created')}
            </LabelTitle>
            <Typography variant="body2">{workspaceChildren.length}</Typography>
          </Box>
        </Box>
      )}

      {/* Child workspaces table */}
      <ChildWorkspacesTable
        childData={childData}
        currentWorkspaceId={workspace.id}
        showStoresColumn={showStoresColumn}
        onEditQuota={(childId, users, devices) =>
          setEditQuota({ childId, users, devices })
        }
      />

      {/* Edit Quota Dialog */}
      <Dialog open={!!editQuota} onClose={() => setEditQuota(null)}>
        <DialogTitle>
          {polyglot.t('stoerk_id.subworkspaces.edit_quota_title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={polyglot.t('stoerk_id.subworkspaces.user_quota')}
            type="number"
            value={editQuota?.users ?? 0}
            onChange={(e) =>
              setEditQuota((prev) =>
                prev ? { ...prev, users: Number(e.target.value) } : null
              )
            }
            margin="normal"
            fullWidth
            disabled={isSavingQuota}
          />
          <TextField
            label={polyglot.t('stoerk_id.subworkspaces.device_quota')}
            type="number"
            value={editQuota?.devices ?? 0}
            onChange={(e) =>
              setEditQuota((prev) =>
                prev ? { ...prev, devices: Number(e.target.value) } : null
              )
            }
            margin="normal"
            fullWidth
            disabled={isSavingQuota}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditQuota(null)}
            disabled={isSavingQuota}
            variant="text"
          >
            {polyglot.t('button.cancel')}
          </Button>
          <LoadingButton
            onClick={handleSaveQuota}
            variant="contained"
            loading={isSavingQuota}
          >
            {polyglot.t('button.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Internal table component for child workspaces */
function ChildWorkspacesTable({
  childData,
  currentWorkspaceId,
  showStoresColumn,
  onEditQuota,
}: {
  childData: {
    childId: string;
    workspace: WorkspaceAPI | undefined;
    usage: StoerkIdUsageAPIResponse | undefined;
    usageResponse:
      | ReturnType<
          ReturnType<typeof StoerkIdAPI.endpoints.getWorkspaceUsage.select>
        >
      | undefined;
  }[];
  currentWorkspaceId: string;
  showStoresColumn?: boolean;
  onEditQuota: (childId: string, users: number, devices: number) => void;
}) {
  const polyglot = getPolyglot();

  const rightsUserUtil = useUserRightUtil();

  // Fetch stores usage data for children
  const storesUsageDic = useAppSelector((state) => {
    const dic: Record<string, number> = {};
    childData.forEach(({ childId, workspace: ws }) => {
      if (!ws?.settings?.['flexeserveUI.hierarchy']) return;
      const result = StoerkIdAPI.endpoints.getWorkspaceStoresUsage.select({
        workspaceId: childId,
        includeChildWorkspaces: true,
        includeStores: false,
      })(state);
      if (result.data) {
        dic[childId] = result.data.totalStoresCount;
      }
    });
    return dic;
  });

  const columns: LightTableColumDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_name'),
        orderable: true,
      },
      {
        field: 'ownerEmail',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_owner_email'),
        orderable: true,
      },
      {
        field: 'status',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_status'),
        orderable: true,
      },
      {
        field: 'commanders',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_commanders'),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'teamMembers',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_team_members'),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      ...(showStoresColumn
        ? [
            {
              field: 'stores',
              headerName: polyglot.t(
                'stoerk_id.customer_list.header_cell_stores'
              ),
              orderable: true,
              TableCellProps: { align: 'right' as const },
            },
          ]
        : []),
      {
        field: 'actions',
        headerName: '',
        TableCellProps: { align: 'center', width: '56px' },
      },
    ],
    [polyglot, showStoresColumn]
  );

  const rows = useMemo(
    () =>
      childData.map(({ childId, workspace: ws, usage, usageResponse }) => {
        const individualLabel = polyglot.t(
          'stoerk_id.subworkspaces.individual'
        );
        const name = ws?.name ?? childId;
        const commandersUsed =
          usage?.commandersRootWorkspaceCount ??
          usage?.commanderCount ??
          usage?.commanders?.length ??
          0;
        const commanderIndividual =
          usage?.commandersExclusiveToRootWorkspaceCount ?? 0;
        const commandersBreakdown =
          commanderIndividual !== commandersUsed
            ? ` (${commanderIndividual} ${individualLabel})`
            : '';
        const commandersAllocated = ws?.quota.devices ?? 0;
        const teamMembersUsed = usage?.userCount ?? usage?.users?.length ?? 0;
        const teamMembersAllocated = ws?.quota.users ?? 0;
        const loading = usageResponse?.isLoading;
        const showEdit =
          ws?.parentId &&
          rightsUserUtil.canEditWorkspace(ws?.parentId) &&
          currentWorkspaceId !== childId;
        const showLink =
          rightsUserUtil.canViewWorkspace(currentWorkspaceId) &&
          currentWorkspaceId !== childId;
        return {
          id: childId,
          created: ws?.created,
          name: {
            value: name,
            element: showLink ? (
              <Link to={`account/${childId}`} component={LinkRouter}>
                {name}
              </Link>
            ) : (
              name
            ),
          },
          ownerEmail: ws?.accountHolderId ? (
            <OwnerEmailCell userId={ws?.accountHolderId} />
          ) : (
            '-'
          ),
          status: {
            value: ws ? 'Active' : 'Unknown',
            element: (
              <LabelState variant={ws ? 'success' : 'info'}>
                {ws
                  ? polyglot.t('stoerk_id.subworkspaces.status_active')
                  : polyglot.t('stoerk_id.subworkspaces.status_unknown')}
              </LabelState>
            ),
          },
          commanders: {
            value: commandersUsed,
            element: loading ? (
              <Skeleton width={40} sx={{ ml: 'auto' }} />
            ) : (
              `${commandersUsed}${commandersBreakdown} / ${commandersAllocated}`
            ),
          },
          teamMembers: {
            value: teamMembersUsed,
            element: loading ? (
              <Skeleton width={40} sx={{ ml: 'auto' }} />
            ) : (
              `${teamMembersUsed} / ${teamMembersAllocated}`
            ),
          },
          ...(showStoresColumn
            ? {
                stores: {
                  value: storesUsageDic[childId] ?? 0,
                  element: loading ? (
                    <Skeleton width={40} sx={{ ml: 'auto' }} />
                  ) : (
                    storesUsageDic[childId] ?? ''
                  ),
                },
              }
            : {}),
          actions: showEdit ? (
            <ActionsMenu
              onEditQuota={() =>
                onEditQuota(
                  childId,
                  ws?.quota.users ?? 0,
                  ws?.quota.devices ?? 0
                )
              }
            />
          ) : null,
        };
      }),
    [
      childData,
      currentWorkspaceId,
      polyglot,
      onEditQuota,
      showStoresColumn,
      storesUsageDic,
    ]
  );

  return (
    <LightTable
      columns={columns}
      rows={rows}
      variant="clear"
      orderByDefault="status"
    />
  );
}

function ActionsMenu({ onEditQuota }: { onEditQuota: () => void }) {
  const polyglot = getPolyglot();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            onEditQuota();
          }}
        >
          {polyglot.t('stoerk_id.subworkspaces.assign_quota')}
        </MenuItem>
      </Menu>
    </>
  );
}

function OwnerEmailCell({ userId }: { userId: string }) {
  const { data: user } = UserAPI.endpoints.getUser.useQuery(userId);
  return <>{user?.email ?? userId}</>;
}

export default SubWorkspacesOverview;
